import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import styled from 'styled-components';
// eslint-disable-next-line no-unused-vars
import take from 'lodash/take';
import isEmpty from 'lodash/isEmpty';

import NutritionSection from './NutritionSection';
import { Box, Flex, Hr, Text, Image, Icon } from 'core/ui/atoms';

import useComponent from 'dashboard/hooks/useComponent';
import useComponentLazy from 'dashboard/hooks/useComponentLazy';
import { ReactComponent as InfoSVG } from 'core/assets/images/V2/icons/Info.svg';
import { isLoading } from 'core/api/loadingHelpers';
import Loading from 'core/ui/components/Loading';
import { PRODUCT_TEMP } from 'core/assets/images/V2';
import SplitPageSidebar from 'core/ui/components/SplitPageSidebar';
import ResponsiveView from 'core/ui/components/ResponsiveView';

const ProductDescriptionPage = () => {
  const { componentId } = useParams();
  const history = useHistory();
  const { componentDetail = {}, loading } = useComponent(componentId);

  const { variants = [], variantId } = componentDetail;
  const [selectedVariant, setSelectedVariant] = useState(variants?.find((variant) => variant.id === variantId));

  const prevVariantIdRef = useRef();

  const {
    fetchComponent,
    componentDetail: componentVariantDetail,
  } = useComponentLazy();

  useEffect(() => {
    if (
      selectedVariant?.id &&
      prevVariantIdRef.current !== selectedVariant.id
    ) {
      fetchComponent(componentId, selectedVariant.id);
      prevVariantIdRef.current = selectedVariant.id;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant?.id, componentId]);

  const handleGoBack = () => history.goBack();

  if (isLoading(loading, componentDetail)) {
    return <Loading />;
  }

  const component = componentVariantDetail || componentDetail;

  const largeImages = component?.images?.map((image) => image.largeUrl) || [PRODUCT_TEMP];


  const RenderDietTag = () => {
    const { dietTags } = componentDetail;
    return take(dietTags, 3)?.map((tag, index) => {
      return (
        <DietTag key={index}>{tag}</DietTag>
      );
    });
  };

  const RenderImages = () => {
    return largeImages?.map((image, index) => index === 0 && (
      <SelectCont key={index}>
        <ProductImage src={image} alt={component?.name} />
      </SelectCont>
    ));
  };

  const RenderSideBar = () => {
    return (
      <ScrollableContent>
        <RenderImages />
      </ScrollableContent>
    );
  };

  const NavigationHeader = () => (
    <NavigationHeaderStyle
      direction='row'
      justifyContent='flex-start'
      alignItems='center'
      gap='16px'
    >
      <Caret icon='arrow_l' onClick={handleGoBack} />
      <Title>{component?.name}</Title>
    </NavigationHeaderStyle>
  );

  return (
    <Container>
      <ResponsiveView desktop={true}>
        <NavigationHeader />
      </ResponsiveView>
      <SplitPageSidebar sidebarWidth='40%' sidebarView={<RenderSideBar />}>
        <ResponsiveView desktop={false}>
          <NavigationHeader />
        </ResponsiveView>
        <Hr />
        <ResponsiveView desktop={false}>
          <ScrollableContent>
          <RenderImages />
        </ScrollableContent>
        </ResponsiveView>
        <ContentDiv>
          <CalorieText>Cal:{' '}
            {component?.nutrition?.calories}kcal  |
            Protein : {component?.nutrition.proteinGrams}g
          </CalorieText>
          {!isEmpty(componentDetail?.dietTags) &&
            <DietContainer alignItems='center' gap='8px'>
              <InfoSVG />
              <RenderDietTag />
            </DietContainer>
          }
          <SubTitle>{component?.subtitle}</SubTitle>
          <Tagline>{component?.tagLine}</Tagline>
          <NutritionSection
            componentDetail={component}
            setSelectedVariant={setSelectedVariant}
          />
        </ContentDiv>
        <FoodExpiry>
          Consume within {component?.shelfLife} days of delivery
        </FoodExpiry>
      </SplitPageSidebar>
    </Container>
  );
};

const NavigationHeaderStyle = styled(Flex)`
  padding-left: 20px;
  ${(p) => p.theme.max('sm')`
    padding-left: 0px;
  `}
`;

const CalorieText = styled(Text)`
  color: #36443A;
  font-family: ${({ theme }) => theme.typography.familyLarken};
  font-size: 22px;
  font-style: normal;
  font-weight: 400;
  line-height: 28px;
`;

const SubTitle = styled(Text)`
  color: #36443A;
  font-family: ${({ theme }) => theme.typography.familyLarken};
  font-size: 16px;
  font-style: italic;
  font-weight: 400;
  line-height: 26px;
`;

const Tagline = styled(Text)`
  font-family: ${({ theme }) => theme.typography.familyPoppins}
  font-size: 14px;
  line-height: 26px;
`;

const FoodExpiry = styled(Box)`
  background: #36443A;
  color: #FFF;
  text-align: center;
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  padding-top: 15px;
  padding-bottom: 15px;
`;

const Caret = styled(Icon)`
  display: inline-block;
  font-size: larger;
  transition: transform 0.25s ease-in-out;
  transform-style: preserve-3d;
  margin-left: 20px;
  transform: rotateX(${(p) => p.$open ? '180deg' : '0deg' });
`;

const ContentDiv = styled(Box)`
  padding-left: 16px;
  padding-right: 16px;
`;

const DietContainer = styled(Flex)`
  margin-bottom: 20px;
`;

const DietTag = styled(Box)`
  border-radius: 4px;
  background: #C5D1C0;
  display: flex;
  padding: 6px 8px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  width: fit-content;
  color: ${(p) => p.theme.colors.primaryV2};
  text-align: center;
  leading-trim: both;
  text-edge: cap;
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
`;

const SelectCont = styled.div`
  position: relative;
  height: 250px;
  width: 250px;
  display: flex;
  justify-content: end;
  flex-shrink: 0;
  margin-right: 10px;
  &:first-child {
    margin-left: 8%;
  }
  &:last-child {
    margin-right: 15%;
  }
`;

const Title = styled(Text)`
  color: ${(p) => p.theme.colors.primaryV2};
  font-family: ${({ theme }) => theme.typography.familyLarken};
  font-size: 30px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  letter-spacing: 0.9px;
  margin-top: 10px;
  margin-bottom: 10px;
  width: 100%;
  text-align: center;
  ${(p) => p.theme.max('sm')`
    width: 100%;
    text-align: unset;
  `}
`;

const Container = styled(Box)`
  margin-top: 70px;
`;

const ProductImage = styled(Image)`
  height: 100%;
  width: 100%;
  object-fit: cover;
`;

const ScrollableContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  overflow-x: auto;
  width: 100%;
  gap: 24px;
  padding-bottom: 10px; /* Space for scrollbar */
  scrollbar-width: none;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  overflow-y: auto;
  column-gap: 25px;

  // &:first-child {
  //   margin-left: 20%;
  // }
  // &:last-child {
  //   margin-right: 15%;
  // }

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    height: 6px;
  }

  /* Track */
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }

  /* Handle on hover */
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }

  /* For Firefox */
  scrollbar-color: #888 #f1f1f1;
`;

export default ProductDescriptionPage;
